import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { EDDY_MACHINE_FLAG, EDDY_AUDIO_ANSWERS_FLAG, PRO_BUILDER_FLAG } from '~/lib/feature-flag-names';

/** Flag name for in-chat image generation — mirrors the backend flag registry. */
export const IMAGE_GENERATION_FLAG = 'eddy.image-generation';
/** FRONT-2217: org kill switch over Ask Eddy in published apps. */
export const APPS_ASK_FLAG = 'eddy.apps-ask';
/** FRONT-2219: org kill switch over the coding session's built-in tool groups. */
export const CODING_CAPABILITIES_FLAG = 'eddy.coding-capabilities';
/** FRONT-2382: the coding "New project" form's optional "Design direction" section (inspiration text, reference uploads, web-suggested directions) — mirrors the backend flag registry. */
export const CODING_DESIGN_DIRECTION_FLAG = 'eddy.coding-design-direction';
/** FRONT-2670: users see compute blocks instead of tokens — mirrors the backend flag registry. Read with fallback `false`: showing spend to a customer's users is that customer's call. */
export const COMPUTE_BLOCKS_FLAG = 'eddy.compute-blocks';
/** FRONT-3227: the tabbed Inbox (Today, New, To Do, Review, Remember) — mirrors the backend flag registry. Read with fallback `false`: off, or flags not loaded, shows today's single feed. */
export const INBOX_TABS_FLAG = 'eddy.inbox-tabs';

/** FRONT-2937: app editors and drafts — mirrors the backend flag registry. Read with fallback `false`, matching the server's answer for a flag not created yet. */
export const APP_DRAFTS_FLAG = 'eddy.app-drafts';
/** FRONT-3154: EDDY follows up in a chat on its own — mirrors the backend flag registry. Per person; read with fallback `false`: it only changes the Proactive dial's wording, the server is the real gate. */
export const CHAT_FOLLOW_UPS_FLAG = 'eddy.chat-follow-ups';

/** Flag name for the Diagnostics > Knowledge Graph tab (FRONT-1057) — mirrors the backend flag registry. */
export const GRAPH_HEALTH_TAB_FLAG = 'eddy.graph-health-tab';

/** Flag name for the left sidebar's default collapsed state (FRONT-1332) — mirrors the backend flag registry. */
export const SIDEBAR_DEFAULT_COLLAPSED_FLAG = 'eddy.sidebar-default-collapsed';

/** Flag name for the Files-screen Folders feature + its scoped chat (FRONT-776) — mirrors the backend flag registry. */
export const FILE_FOLDERS_FLAG = 'eddy.file-folders';

/** Flag name for the Knowledge section's company knowledge bases (FRONT-1460) — mirrors the backend flag registry. */
export const KNOWLEDGE_SERVICE_FLAG = 'eddy.knowledge-service';

/** FRONT-2633: project members — mirrors the backend flag registry. The server is the real gate; read with fallback `false`. */
export const SHARED_PROJECTS_FLAG = 'eddy.shared-projects';

/** Flag name for the Sketches gallery, a tab on the Knowledge page since FRONT-1549 — mirrors the backend flag registry. */
export const SKETCHES_FLAG = 'eddy.sketches';
export const SLIDE_DECKS_FLAG = 'eddy.slide-decks';

/** Flag name for the Canvas side panel in the chat window (FRONT-2594) — mirrors the backend flag registry. Read with fallback `false`. */
export const ARTIFACT_WORKSPACE_FLAG = 'eddy.artifact-workspace';

/** Flag name for the composer model picker (FRONT-1698) — mirrors the backend flag registry. Read with fallback `false`. */
export const MODEL_PICKER_COMPOSER_FLAG = 'eddy.model-picker-composer';

/** Flag name for the org-shared MCP servers (FRONT-1450) — mirrors the backend flag registry. The backend is its only gate; the frontend reads it only to keep the What's new note from describing them to a workspace without it. Read with fallback `false`. */
export const ORG_MCP_SERVERS_FLAG = 'eddy.org-mcp-servers';

/** Flag name for the hands-free voice call (FRONT-254) — mirrors the backend flag registry. Read with fallback `false`: off keeps every call exactly as before (no minimize, no call strip); the server checks the same flag before it offers any hands-free tool or screen note. */
export const VOICE_HANDS_FREE_FLAG = 'eddy.voice-hands-free';

/** Flag name for the Slack app (FRONT-2199) — mirrors the backend flag registry. The backend is its only gate; the frontend reads it only to keep the What's new note from describing Slack to a workspace without it. Read with fallback `false`. */
export const CHANNEL_SLACK_FLAG = 'eddy.channel-slack';

/** Flag name for coding spend on Diagnostics and Settings > Compute & spend (FRONT-1612) — mirrors the backend flag registry. The backend is its only gate; the frontend reads it only to keep the What's new note from describing coding cost to a workspace without it. Read with fallback `false`. */
export const CODING_SPEND_FLAG = 'eddy.coding-spend';

/** Flag name for the website import (FRONT-2982/3012/3018) — mirrors the backend flag registry. Internal until a legal and compliance check clears it: off hides "Import a website", the imports tab and its What's new line; the server answers 404 on every import route. Read with fallback `false`. */
export const WEBSITE_IMPORT_FLAG = 'eddy.website-import';

/**
 * Flag name for the Inspector panel + its own header toggle (FRONT-1323) —
 * mirrors the backend flag registry.
 *
 * Read with fallback `true`, the same shape as the New-chat kill switch above.
 * Merging this therefore changes nothing on its own: the Inspector keeps
 * showing until someone creates `eddy.show-inspector` in Unleash and switches
 * it off. That is deliberate. Switching it off is what removes the only
 * cross-page signal that an approval is waiting (the header badge below can
 * then only reach approvals raised from a chat), so it should be a conscious
 * call with that trade-off in view, not a side effect of a deploy.
 */
export const SHOW_INSPECTOR_FLAG = 'eddy.show-inspector';

/**
 * Flag name for per-tenant model grants (FRONT-1702) — mirrors the backend
 * flag registry.
 *
 * Read with fallback `false`, unlike the kill switches above: the control it
 * hides can add an expensive model to a customer's workspace, and the
 * super-admin role that guards it is granted liberally on some tenants. So
 * before the toggle exists in Unleash the control must be absent, not
 * present. The server refuses both grant routes on the same flag.
 */
export const TENANT_EXTRA_MODELS_FLAG = 'eddy.tenant-extra-models';

/**
 * The PLATFORM's flag for manual graph editing in the workflow editor
 * (PDEV-9741), read as-is rather than mirrored as an `eddy.*` twin
 * (FRONT-2152). `/api/feature-flags` returns every flag Unleash knows for
 * the caller's org, platform-owned ones included, so one switch rolls the
 * feature out on both frontends at once. Not in the backend registry on
 * purpose: that registry lists flags Eddy owns, and this one is owned and
 * retired by the platform.
 *
 * Read with fallback `false`: off keeps every structural change going
 * through the chat agent, the same posture as the platform frontend.
 */
export const WORKFLOW_MANUAL_GRAPH_EDITING_FLAG = 'workflow.manual-graph-editing';

/**
 * The PLATFORM's flag for per-step compute blocks on the workflow canvas
 * (PDEV-10211), read as-is for the same reason as the flag above. Fallback
 * `false`: off shows no step costs, matching the platform frontend.
 */
export const WORKFLOW_STEP_COMPUTE_BLOCKS_FLAG = 'workflow.step-compute-blocks';

/**
 * The PLATFORM's flag for the editor's "Add step" button (PDEV-10380), read
 * as-is like the two above. It only takes effect with manual graph editing
 * on. Fallback `false`: off leaves adding a step to the chat agent.
 */
export const WORKFLOW_MANUAL_NODE_CREATION_FLAG = 'workflow.manual-node-creation';

/**
 * The PLATFORM's flag for the input step in that menu, read as-is like the
 * three above. The workflow service checks the same flag before it takes an
 * answer, so one switch covers offering the step and answering it.
 */
export const WORKFLOW_INPUT_NODE_FLAG = 'workflow.input-node';

/**
 * The PLATFORM's flag for the editor's run history menu, read as-is like the
 * four above, so v1 and Eddy open earlier runs on the same switch.
 */
export const WORKFLOW_RUN_HISTORY_FLAG = 'workflow.run-history';

/**
 * Flag name for Eddy Machine (FRONT-1985) — mirrors the backend flag
 * registry. Unlike every other flag above, this one is constrained per
 * PERSON (`userId`) on the server, not per org — see
 * `src/core/eddy-machine-flag.ts`.
 *
 * Read with fallback `false`: this is a dark release and must be absent
 * until the flag exists in Unleash and is on for this specific person. The
 * server 404s every Eddy Machine route on the same flag, so a briefly-wrong
 * client-side `true` could never reach real data anyway — but the control
 * itself (register/run/see a machine) must not appear before the flag says
 * so, the same "off means gone, not greyed out" posture as
 * `TENANT_EXTRA_MODELS_FLAG` above.
 *
 * Declared in `~/lib/feature-flag-names` (FRONT-1986) — a plain,
 * no-import module — and re-exported here so every existing importer of
 * this hook module keeps working unchanged.
 */
export { EDDY_MACHINE_FLAG, EDDY_AUDIO_ANSWERS_FLAG, PRO_BUILDER_FLAG };

export interface EvaluatedFlag {
  name: string;
  enabled: boolean;
  description?: string | null;
  type?: string | null;
  stale?: boolean;
  project?: string | null;
}

export interface FeatureFlagsResponse {
  /** False when the flag system isn't configured (e.g. local) — everything on. */
  configured: boolean;
  accessFlag: string;
  flags: EvaluatedFlag[];
}

/**
 * Feature flags as evaluated for the current org/user (`/api/feature-flags`,
 * FRONT-325). Read-only — flags are managed in the platform's flag console.
 */
export function useFeatureFlags() {
  return useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => api.get<FeatureFlagsResponse>('/api/feature-flags'),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Pure resolution logic behind `useFeatureFlag`, factored out so it's
 * testable without mocking `useQuery`.
 *
 * `fallback` is returned while loading or on error, and also when the flag
 * has no definition in Unleash yet (not created there, distinct from
 * "created but off") — mirrors the backend's `checkEddyAccess` "flag-missing
 * must not lock anyone out" rule (src/core/feature-flags.ts), so a
 * newly-registered flag doesn't silently hide a feature the moment this
 * code deploys, before anyone's had a chance to create the toggle in Unleash.
 */
export function resolveFeatureFlag(
  name: string,
  fallback: boolean,
  data: FeatureFlagsResponse | undefined,
  isLoading: boolean,
  isError: boolean,
): boolean {
  if (isLoading || isError || !data) return fallback;
  const flag = data.flags?.find((f) => f.name === name);
  // unconfigured (a local stack): every flag on, except one the stack lists as off (EDDY_LOCAL_FLAGS_OFF, FRONT-2950)
  if (!data.configured) return flag ? flag.enabled : true;
  return flag ? flag.enabled : fallback;
}

/**
 * The `isFlagEnabled` the sidebar rail and the command palette hand to
 * `isSidebarDestinationVisible` (FRONT-3003). Fallback is `false`: a flagged
 * destination (Pro Builder) must not show while the flags request is in
 * flight, errored, or has no definition — "off means gone". One definition so
 * the two surfaces cannot disagree, and so a test can pin the fallback.
 */
export function resolveDestinationFlag(
  data: FeatureFlagsResponse | undefined,
  isLoading: boolean,
  isError: boolean,
): (flag: string) => boolean {
  return (flag) => resolveFeatureFlag(flag, false, data, isLoading, isError);
}

/**
 * Whether a named flag is enabled for the current person (their org and
 * their user id — most flags only constrain on the org, but a per-person
 * flag like `eddy.machine` also constrains on the user id, and this same
 * evaluation covers both). Mirrors the backend's
 * semantics: unconfigured flag system → everything enabled; configured →
 * enabled only if the flag is present and on. See `resolveFeatureFlag` for
 * the resolution rules. Default `false` (fail-closed); pass `true` for a
 * feature that should stay visible on a flags-endpoint hiccup or before the
 * toggle exists in Unleash, since the server still gates the actual action.
 */
export function useFeatureFlag(name: string, fallback = false): boolean {
  const { data, isLoading, isError } = useFeatureFlags();
  return resolveFeatureFlag(name, fallback, data, isLoading, isError);
}

/**
 * Pure resolution for `EDDY_MACHINE_FLAG`, factored out the same way
 * `resolveFeatureFlag` is so it's testable without mocking `useQuery`.
 * Fallback is `false` — see the constant's own doc comment for why.
 */
export function resolveEddyMachineEnabled(
  data: FeatureFlagsResponse | undefined,
  isLoading: boolean,
  isError: boolean,
): boolean {
  return resolveFeatureFlag(EDDY_MACHINE_FLAG, false, data, isLoading, isError);
}

/** Whether Eddy Pro Builder (FRONT-3003) is on for the current person. Fallback `false` (dark release); unconfigured flag system (local) resolves ON via `resolveFeatureFlag`, exactly like the Machine. */
export function useProBuilderEnabled(): boolean {
  const { data, isLoading, isError } = useFeatureFlags();
  return resolveFeatureFlag(PRO_BUILDER_FLAG, false, data, isLoading, isError);
}

/** Whether Eddy Machine is enabled for the current person. Consumed by `machine-section.tsx` (the Settings → Machine gate) and, via `resolveEddyMachineStatus` below, `useEddyMachineStatus`. */
export function useEddyMachineEnabled(): boolean {
  const { data, isLoading, isError } = useFeatureFlags();
  return resolveEddyMachineEnabled(data, isLoading, isError);
}

/**
 * Whether the Eddy Machine flag is on, off, or not yet known — for a caller
 * that must not treat "not yet known" as "off" (FRONT-2278).
 *
 * A plain boolean can't carry that third state, and `useEddyMachineEnabled`
 * above deliberately collapses it to `false` for its one caller's gating
 * needs. But `operator-panel-host.tsx`'s deep-link effect strips the
 * `operatorRun` URL param the first time it runs — if that first run reads
 * "off" because the flag simply hasn't answered yet, the param is gone
 * before the flag ever resolves, and the operator panel this link exists to
 * open never appears (FRONT-2278). `FeatureFlagStatus` gives that caller a
 * `'loading'` value to hold the link open on instead of guessing.
 */
export type FeatureFlagStatus = 'loading' | 'enabled' | 'disabled';

/**
 * Pure resolution behind `useEddyMachineStatus`, factored out the same way
 * `resolveEddyMachineEnabled` is.
 *
 * Order matters: `isError` is checked first because it's definitive — once
 * TanStack has exhausted its retries there is no more data coming, and we
 * fail closed the same way `resolveFeatureFlag` does. Otherwise `data ===
 * undefined` decides "not yet known".
 *
 * It deliberately does NOT take a loading flag, unlike its
 * `resolveEddyMachineEnabled` sibling: `isLoading === false` with no data
 * and no error is a reachable state on its own (a query paused because the
 * browser is offline, or a disabled one), so branching on it would misread
 * that as "answered, and the answer is no" — reproducing the exact race
 * this type exists to fix. Leaving it out of the signature makes that
 * mistake impossible rather than merely tested-against. `data === undefined`
 * is the honest, implementation-independent definition of "we haven't heard
 * back yet" — it can't rot if TanStack's internal loading-flag semantics
 * ever change.
 */
export function resolveEddyMachineStatus(
  data: FeatureFlagsResponse | undefined,
  isError: boolean,
): FeatureFlagStatus {
  if (isError) return 'disabled';
  if (data === undefined) return 'loading';
  return resolveEddyMachineEnabled(data, false, false) ? 'enabled' : 'disabled';
}

/** Three-state read of Eddy Machine's availability for the current person — see `FeatureFlagStatus` for why a boolean isn't enough. */
export function useEddyMachineStatus(): FeatureFlagStatus {
  const { data, isError } = useFeatureFlags();
  return resolveEddyMachineStatus(data, isError);
}
